import { ApiError, type HqApi, type UserProfile } from '@hq/api-sdk';
import { prepareImageUpload } from '@hq/utils';
import { CameraOutlined, LockOutlined } from '@ant-design/icons';
import {
  App,
  Avatar,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Space,
  Tabs,
  Tag,
  Typography,
  Upload
} from 'antd';
import { useEffect, useState } from 'react';
import SelfQrPanel from './SelfQrPanel';
import type { AccountProfileView, ProfileTab } from './types';

export interface ProfileDrawerProps {
  hq: HqApi;
  open: boolean;
  profile: AccountProfileView;
  /** 打开时默认停留的页签（Header 的「修改密码」直接切到密码页）。 */
  tab?: ProfileTab;
  onClose: () => void;
  /** 资料更新成功（头像/邮箱）后回传最新 Profile，调用方据此刷新本地会话。 */
  onUpdated?: (profile: UserProfile) => void;
  /** 展示「我的二维码」页签（目前仅院方端开启）。 */
  showSelfQr?: boolean;
}

const MAX_AVATAR_MB = 5;

/**
 * 个人账户抽屉（五端共用）：个人信息 + 头像 + 修改密码。
 *
 * 口径：
 * · 头像走资源服务 `hq.file.upload`（bizType=USER_AVATAR），拿 fileUrl 再回写 sys_user.avatar_url；
 * · 手机号只读（后端只回脱敏值，改号涉及登录与安全，走管理员）；
 * · 邮箱可自助维护（用于找回口令），改完回传最新 Profile，各端 patchProfile 刷新头部展示。
 */
export default function ProfileDrawer({ hq, open, profile, tab = 'info', onClose, onUpdated, showSelfQr }: ProfileDrawerProps) {
  const { message } = App.useApp();
  const [activeKey, setActiveKey] = useState<ProfileTab>(tab);
  const [uploading, setUploading] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [remote, setRemote] = useState<UserProfile | null>(null);
  const [email, setEmail] = useState(profile.email ?? '');
  const [form] = Form.useForm<{ oldPassword: string; newPassword: string; confirm: string }>();

  // 打开时拉一次 /auth/me：本地会话只缓存登录瞬间的字段，
  // 头像/科室/手机/邮箱以服务端实时值为准（也避免各端 store 各存一份）。
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    hq.auth
      .me()
      .then((me) => {
        if (alive) setRemote(me);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [open, hq]);

  useEffect(() => {
    if (open) setActiveKey(tab);
  }, [open, tab]);

  // 本地会话兜底 + 服务端实时值覆盖
  const view: AccountProfileView = {
    userId: String(remote?.userId ?? profile.userId),
    userCode: remote?.userCode ?? profile.userCode,
    name: remote?.realName ?? profile.name,
    orgName: remote?.orgName ?? profile.orgName,
    deptName: remote?.deptName ?? profile.deptName,
    mobile: remote?.mobile ?? profile.mobile,
    email: remote?.email ?? profile.email,
    avatarUrl: remote?.avatarUrl ?? profile.avatarUrl,
    level: remote?.level ?? profile.level,
    levelText: profile.levelText
  };

  useEffect(() => {
    setEmail(view.email ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.email, open]);

  const avatarText = (view.name ?? view.userCode ?? '?').trim().slice(0, 1);

  const uploadAvatar = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('请选择图片文件');
      return false;
    }
    if (file.size > MAX_AVATAR_MB * 1024 * 1024) {
      message.error(`图片不能超过 ${MAX_AVATAR_MB}MB`);
      return false;
    }

    setUploading(true);
    try {
      // 资源服务不处理图片：客户端先缩到 512 长边并转 JPEG（头像不需要原图，通常 <200KB）
      const prepared = await prepareImageUpload(file, { maxEdge: 512, quality: 0.85, preferJpeg: true });
      const dto = await hq.file.upload({
        file: prepared.file,
        fileName: prepared.fileName,
        width: prepared.width,
        height: prepared.height,
        isPublic: true,
        bizType: 'USER_AVATAR',
        bizField: 'avatarUrl'
      });
      const next = await hq.auth.updateProfile({ avatarUrl: dto.fileUrl });
      onUpdated?.(next);
      message.success('头像已更新');
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '头像上传失败，请重试');
    } finally {
      setUploading(false);
    }
    // 交给我们自己上传（返回 false 阻止 antd 默认请求）
    return false;
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const next = await hq.auth.updateProfile({ email: email.trim() });
      onUpdated?.(next);
      message.success('邮箱已保存');
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '保存失败，请重试');
    } finally {
      setSavingEmail(false);
    }
  };

  const changePassword = async (values: { oldPassword: string; newPassword: string; confirm: string }) => {
    setSavingPwd(true);
    try {
      await hq.auth.changePassword({ oldPassword: values.oldPassword, newPassword: values.newPassword });
      message.success('密码已修改，下次登录请使用新密码');
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '修改失败，请重试');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <Drawer
      title="个人账户"
      width={480}
      open={open}
      onClose={onClose}
      styles={{ body: { paddingTop: 16 } }}
    >
      <Space size={16} align="center" style={{ marginBottom: 16 }}>
        <Upload showUploadList={false} accept="image/*" beforeUpload={(file) => uploadAvatar(file as File)}>
          <div style={{ position: 'relative', cursor: 'pointer' }} title="点击更换头像">
            <Avatar size={64} src={view.avatarUrl || undefined} style={{ background: '#1677ff', fontSize: 24 }}>
              {avatarText}
            </Avatar>
            <span
              style={{
                position: 'absolute',
                right: -2,
                bottom: -2,
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#fff',
                border: '1px solid #d9d9d9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: '#666'
              }}
            >
              <CameraOutlined />
            </span>
          </div>
        </Upload>
        <div>
          <Typography.Text strong style={{ fontSize: 16 }}>
            {view.name ?? view.userCode ?? '-'}
          </Typography.Text>
          <div>
            <Typography.Text type="secondary">工号 {view.userCode ?? '-'}</Typography.Text>
            {view.levelText ? (
              <Tag color="blue" style={{ marginInlineStart: 8 }}>
                {view.levelText}
              </Tag>
            ) : null}
          </div>
        </div>
      </Space>

      {uploading ? <Typography.Text type="secondary">头像上传中…</Typography.Text> : null}

      <Tabs
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key as ProfileTab)}
        items={[
          {
            key: 'info',
            label: '个人信息',
            children: (
              <>
                <Descriptions column={1} size="small" bordered styles={{ label: { width: 92 } }}>
                  <Descriptions.Item label="所属机构">{view.orgName ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="所属科室">{view.deptName ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="手机号">{view.mobile ?? '-'}</Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 16 }}>
                  <Typography.Text type="secondary">邮箱（用于找回口令）</Typography.Text>
                  <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                    <Input
                      value={email}
                      placeholder="name@example.com"
                      onChange={(event) => setEmail(event.target.value)}
                      allowClear
                    />
                    <Button type="primary" loading={savingEmail} onClick={() => void saveEmail()}>
                      保存
                    </Button>
                  </Space.Compact>
                </div>
                <Typography.Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
                  头像与邮箱为自助维护；手机号与所属机构如需变更，请联系管理员在后台调整。
                </Typography.Paragraph>
              </>
            )
          },
          {
            key: 'password',
            label: '修改密码',
            children: (
              <Form form={form} layout="vertical" onFinish={(values) => void changePassword(values)} requiredMark={false}>
                <Form.Item
                  name="oldPassword"
                  label="原密码"
                  rules={[{ required: true, message: '请输入原密码' }]}
                >
                  <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
                </Form.Item>
                <Form.Item
                  name="newPassword"
                  label="新密码"
                  rules={[
                    { required: true, message: '请输入新密码' },
                    { min: 8, max: 64, message: '密码长度需为 8~64 位' }
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  label="确认新密码"
                  dependencies={['newPassword']}
                  rules={[
                    { required: true, message: '请再次输入新密码' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        return !value || getFieldValue('newPassword') === value
                          ? Promise.resolve()
                          : Promise.reject(new Error('两次输入的新密码不一致'));
                      }
                    })
                  ]}
                >
                  <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={savingPwd} block>
                  保存新密码
                </Button>
                <Typography.Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
                  密码有效期 90 天，到期需重新设置；忘记原密码请联系管理员重置。
                </Typography.Paragraph>
              </Form>
            )
          },
          ...(showSelfQr
            ? [
                {
                  key: 'qrcode',
                  label: '我的二维码',
                  children: (
                    <SelfQrPanel
                      hq={hq}
                      active={open && activeKey === 'qrcode'}
                      userName={view.name ?? view.userCode}
                      onClose={onClose}
                    />
                  )
                }
              ]
            : [])
        ]}
      />
    </Drawer>
  );
}
